import { pointer, ScaleLinear } from 'd3';
import React, { MouseEvent } from 'react';

import { PriceChangesTooltipData } from '../../PriceChangesChart.types';

export interface PriceChangesChartOverlayProps {
  width: number;
  height: number;
  xScale: ScaleLinear<number, number>;
  uniqueDates: Date[];
  dateToIndexMap?: Map<number, number>;
  setTooltipData: React.Dispatch<
    React.SetStateAction<PriceChangesTooltipData | undefined>
  >;
}

export const PriceChangesChartOverlay = ({
  width,
  height,
  xScale,
  uniqueDates,
  dateToIndexMap,
  setTooltipData,
}: PriceChangesChartOverlayProps) => {
  const handleMouseMove = (event: MouseEvent) => {
    if (uniqueDates.length === 0) {
      return;
    }

    const mouseX = pointer(event)[0];

    if (dateToIndexMap) {
      const hoveredIndex = Math.round(xScale.invert(mouseX));
      const clampedIndex = Math.max(
        0,
        Math.min(hoveredIndex, uniqueDates.length - 1)
      );
      const tooltipDate = uniqueDates[clampedIndex];

      if (!tooltipDate) {return;}

      const index = dateToIndexMap.get(tooltipDate.getTime());

      if (index === undefined) {return;}

      setTooltipData({ xPos: xScale(index), date: tooltipDate });
      return;
    }

    const hoveredTimestamp = xScale.invert(mouseX);
    let closest = uniqueDates[0];
    let closestDist = Math.abs(closest.getTime() - hoveredTimestamp);

    for (const date of uniqueDates) {
      const dist = Math.abs(date.getTime() - hoveredTimestamp);

      if (dist < closestDist) {
        closest = date;
        closestDist = dist;
      }
    }

    setTooltipData({
      xPos: xScale(closest.getTime()),
      date: closest,
    });
  };

  const handleMouseLeave = () => {
    setTooltipData(undefined);
  };

  return (
    <rect
      data-testid="price-changes-chart-overlay"
      width={Math.max(0, width)}
      height={Math.max(0, height)}
      fill="none"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      pointerEvents="all"
    />
  );
};
